// Breadcrumb automatici browser: click, navigazione (history/popstate) e
// console.warn/error. Le richieste HTTP vivono in `http.ts` (instrumentation
// unificata breadcrumb+performance). Ogni sotto-integrazione è gateata da
// `autoBreadcrumbs.{click,navigation,console}`; `install…` ritorna la funzione
// di disinstallazione che ripristina i patch.

import type { Client } from '../../client'
import { subscribeNavigation } from './navigation'

const MAX_MESSAGE = 200

/// Installa le sotto-integrazioni abilitate dalle opzioni.
export function installBreadcrumbs(client: Client): () => void {
  const auto = client.options.autoBreadcrumbs
  const uninstalls = [
    auto.click ? installClicks(client) : noop,
    auto.navigation ? installNavigation(client) : noop,
    auto.console ? installConsole(client) : noop,
  ]
  return () => uninstalls.forEach((fn) => fn())
}

const noop = (): void => {}

// ------------------------------------------------------------------- click

function installClicks(client: Client): () => void {
  if (typeof document === 'undefined') return noop
  const handler = (event: Event): void => {
    const target = event.target
    if (!isElement(target)) return
    client.addBreadcrumb({
      category: 'ui.click',
      type: 'default',
      message: syntheticSelector(target),
    })
  }
  document.addEventListener('click', handler, true)
  return () => document.removeEventListener('click', handler, true)
}

/// Selettore sintetico e privo di PII: tag + id + prime classi + data-test(id).
export function syntheticSelector(el: Element): string {
  let selector = el.tagName.toLowerCase()
  if (el.id) selector += `#${el.id}`
  const classAttr = el.getAttribute('class') ?? ''
  const classes = classAttr.trim().split(/\s+/).filter(Boolean).slice(0, 3)
  if (classes.length > 0) selector += `.${classes.join('.')}`
  const testId = el.getAttribute('data-testid') ?? el.getAttribute('data-test')
  if (testId) selector += `[data-testid="${testId}"]`
  return selector
}

// -------------------------------------------------------------- navigation

// Consumer del patch condiviso (`navigation.ts`): traduce ogni cambio di URL in
// un breadcrumb `navigation`, con from/to privati di query e hash. Il patch
// della History è installato una sola volta anche quando coesiste coi pageview.
function installNavigation(client: Client): () => void {
  return subscribeNavigation((to, from) => {
    client.addBreadcrumb({
      category: 'navigation',
      type: 'navigation',
      data: { from: stripUrl(from ?? to), to: stripUrl(to) },
    })
  })
}

/// Rimuove query string e hash da un URL (PII-safe): resta origin + pathname.
export function stripUrl(href: string): string {
  try {
    const base = typeof location !== 'undefined' ? location.href : undefined
    const url = new URL(href, base)
    return url.origin + url.pathname
  } catch {
    return (href.split('?')[0] ?? href).split('#')[0] ?? href
  }
}

// ----------------------------------------------------------------- console

function installConsole(client: Client): () => void {
  if (typeof console === 'undefined') return noop

  const original = { warn: console.warn, error: console.error }

  const patch = (level: 'warning' | 'error', method: 'warn' | 'error'): void => {
    const originalMethod = original[method]
    console[method] = function patched(...args: unknown[]): void {
      client.addBreadcrumb({
        category: 'console',
        level,
        message: stringifyArgs(args),
      })
      originalMethod.apply(console, args)
    }
  }
  patch('warning', 'warn')
  patch('error', 'error')

  return () => {
    console.warn = original.warn
    console.error = original.error
  }
}

function stringifyArgs(args: unknown[]): string {
  const message = args
    .map((arg) => {
      if (typeof arg === 'string') return arg
      if (arg instanceof Error) return arg.message
      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(' ')
  return message.length > MAX_MESSAGE ? `${message.slice(0, MAX_MESSAGE)}…` : message
}

// ------------------------------------------------------------------ shared

function isElement(value: EventTarget | null): value is Element {
  return typeof Element !== 'undefined' && value instanceof Element
}
